import { Injectable } from "@angular/core";
import { CoreLogService } from "../../services/log.service";
import { environment } from "../../../../environments/environment";
import { CircularInjectionHelper } from "../../services/circularInjectionHelper";
import { LikeService } from "../../services/like.service";
import { ContentType, RegistrationInputFieldIDs } from "../../shared/contentTypes";
import { CustomerInputFieldViewModel, WebshopSettingsDataModel2, WebshopSettingsDMKey } from "../../shared/models";
import { RegistrationDataViewModel } from "../../shared/models/registration.model";
import Utils from "../../shared/utils";
import { IObjectObserver } from "../../services/IObjectObserver";
import { Observable } from "rxjs";
import { ObjectObserverFactory } from "../../services/objectObserverFactory";

@Injectable({
  providedIn: "root"
})
export class RegistrationDataService {


  private _webshopSettingsObsServ: IObjectObserver<WebshopSettingsDataModel2>;
  public get webshopSettingsDataModelObserveChanges(): Observable<(string | number)[]> {
    return this._webshopSettingsObsServ.ObserveChanges;
  }
  constructor(private _coreLogService: CoreLogService
    , private _likeService: LikeService
    , private _circularInjectionHelper: CircularInjectionHelper) {
    this._webshopSettingsObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<WebshopSettingsDataModel2>(ContentType.WebshopSettings, _circularInjectionHelper);
  }



  public async loadAndCreateRegistrationFields(): Promise<CustomerInputFieldViewModel[]> {
    try {
      const webshopSettingsDataModel = await this._webshopSettingsObsServ.GetObject(WebshopSettingsDMKey);
      const jasonInputs = JSON.parse(webshopSettingsDataModel.patron_registration_input_fields) as Object[];
      return this.createRegistrationInputFields(jasonInputs);
    } catch (error) {
      this._circularInjectionHelper.coreLogService.error("RegistrationDataService.loadAndCreateRegistrationFields", "._webshopSettingsObsServ.GetObject", error);
      throw error;
    }
  }

  /**
   * create Registration Input Fields from server json configuration
   * @param jsonInputs Server-Config: [{ field: number, used: boolean, required: boolean, type: string }]
   * @returns Array von IRegistrationInputField für UI
   */
  public createRegistrationInputFields(jsonInputs: Object[]): CustomerInputFieldViewModel[] {
    const registrationInputFields: CustomerInputFieldViewModel[] = [];
    const activeFields = jsonInputs.filter(j => (j["used"]));

    activeFields.forEach(jsonObject => {
      const fieldName = Utils.EnumName(RegistrationInputFieldIDs, +(jsonObject["field"]));
      if (fieldName) {
        registrationInputFields.push({
          fieldName: fieldName,
          fieldType: jsonObject["type"],
          isRequired: jsonObject["required"],
          value: ""
        });
      }
    });

    return registrationInputFields;
  }

  /**
   * set User Information
   * @param registrationDataViewModel User registration data
   * @param likedLocationIds Locations that user has been chosen as favorite location
   */
  public setUserInfo(registrationDataViewModel: RegistrationDataViewModel, likedLocationIds: number[]): Promise<void> {
    const me = this;
    return new Promise<void>(async (resolve, reject) => {
      try {
        const registrationData: { [key: string]: string | number } = {};
        for (const key in registrationDataViewModel) {
          if (Object.prototype.hasOwnProperty.call(registrationDataViewModel, key)) {
            const value = registrationDataViewModel[key];
            if (value !== undefined && value !== null) {
              registrationData[key] = value;
            }
          }
        }
        // set user registration data and wait for response
        await me._circularInjectionHelper.yourAppServer.User.setUserRegistrationData(registrationData);

        // get active likes and update them
        const activeLikes = await me._likeService.GetContentIDForActiveLikes(ContentType.Location);
        const setLikePromiseList: Promise<void>[] = [];

        // dislike old
        activeLikes.forEach(locationId => {
          if (likedLocationIds.indexOf(locationId) < 0) {
            setLikePromiseList.push(me._likeService.SetLike(false, ContentType.Location, locationId));
          }
        });

        // like new
        likedLocationIds.forEach(locationId => {
          if (activeLikes.indexOf(locationId) < 0) {
            setLikePromiseList.push(me._likeService.SetLike(true, ContentType.Location, locationId));
          }
        });

        await Promise.all(setLikePromiseList);
        resolve();
      } catch (error) {
        me._coreLogService.error("RegistrationDataService.setUserInfo", "try catch", error);
        reject();
      }
    });
  }
}
